/**
 * Models we recommend for running Ask on your own key.
 *
 * Any model a team connects can run Ask — Trodo's harness supplies tool
 * calling and structured output where a model has none. What the harness
 * cannot supply is judgement, and that is what this list is about: these are
 * the families strong enough to plan an investigation, read what the tools
 * return and write an answer we would put our name to. A smaller model works;
 * it is simply not what we recommend.
 *
 * Matched on the model id by FAMILY, not by provider, because the same open
 * model reaches us through many doors — Kimi on Groq, Fireworks, Bedrock or a
 * self-hosted vLLM all arrive with different prefixes but the same name.
 */
const RECOMMENDED = [
  // OpenAI — the full GPT-5 line and its mini; not nano.
  /(^|\/)gpt-5(\.\d+)?(-mini)?(-\d{4}-\d{2}-\d{2})?$/i,
  /(^|\/)gpt-5(\.\d+)?-(pro|chat)/i,
  // Anthropic — Opus, Sonnet and Fable, on any host (Bedrock / Vertex ids too).
  /claude-(opus|sonnet|fable)/i,
  // Google — Pro, and the full (non-lite) Flash of Gemini 3 and later.
  /gemini-[\d.]+-pro(?!-image)(-preview)?(-[\w-]+)?$/i,
  /gemini-([3-9])(\.\d+)?-flash(-preview)?$/i,
  // Open models strong enough for agentic work.
  /kimi-k2/i,
  /qwen3[-.](235b|480b|max|coder-480b)|qwen3-max/i,
  /deepseek-(v3|r1|chat|reasoner)/i,
  /gpt-oss-120b/i,
  /glm-4\.[5-9]|glm-[5-9]/i,
  /llama-4-maverick/i,
  /mistral-large|magistral-medium/i,
  /grok-4/i,
];

// Whatever a pattern above says, these are not chat models.
const NOT_CHAT = /image|imagen|embed|tts|audio|speech|whisper|transcribe|realtime|live|lyria|veo|moderation|guard/i;

export function isRecommendedAskModel(modelId) {
  const id = String(modelId || '');
  if (!id || NOT_CHAT.test(id)) return false;
  return RECOMMENDED.some((re) => re.test(id));
}

/** Recommended first, then the rest, as BrandDropdown groups. */
export function groupAskModels(models) {
  const list = (models || []).map(String);
  const recommended = list.filter(isRecommendedAskModel);
  const other = list.filter((m) => !isRecommendedAskModel(m));
  const toOpt = (m) => ({ value: m, label: m, monospace: true });
  if (!recommended.length) return other.map(toOpt);
  return [
    { groupLabel: 'Recommended for Lucid', options: recommended.map(toOpt) },
    ...(other.length ? [{ groupLabel: 'Other models', options: other.map(toOpt) }] : []),
  ];
}
